import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface PricingCardProps {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
  index?: number;
}

export function PricingCard({
  name,
  price,
  description,
  features,
  popular = false,
  index = 0,
}: PricingCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
      className={cn(
        "relative rounded-3xl border p-8 md:p-10 transition-all duration-300",
        popular
          ? "bg-primary text-primary-foreground border-primary scale-105 shadow-premium-lg"
          : "bg-card border-border hover:border-foreground/20 hover:shadow-premium"
      )}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-foreground text-background text-xs font-semibold px-4 py-1.5 rounded-full">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-2">{name}</h3>
        <p className={cn("text-sm mb-4", popular ? "text-primary-foreground/80" : "text-muted-foreground")}>
          {description}
        </p>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl md:text-5xl font-bold">{price}</span>
          <span className={cn("text-sm", popular ? "text-primary-foreground/80" : "text-muted-foreground")}>
            /month
          </span>
        </div>
      </div>

      <ul className="space-y-4 mb-8">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className={cn(
              "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
              popular ? "bg-primary-foreground/20" : "bg-secondary"
            )}>
              <Check className="w-3 h-3" />
            </div>
            <span className={cn("text-sm", popular ? "text-primary-foreground/90" : "text-muted-foreground")}>
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <Link to="/contact">
        <Button
          className={cn(
            "w-full rounded-full",
            popular
              ? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              : ""
          )}
          variant={popular ? "default" : "outline"}
          size="lg"
        >
          Get Started
        </Button>
      </Link>
    </motion.div>
  );
}
